import { useContext, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import robotImgMd from "../../../asset/images/robotImgMd.png";
import selectIcon from "../../../asset/images/selectIcon.png";
import { AuthContext } from "../../../common/components/general/TSAuthProvider";
import { useTSData } from "../../../common/components/general/TSDataContext";
import TSGrid from "../../../common/components/ui/TSGrid";
import { MESSAGING_MODE } from "../../../common/constants/generalConstants";
import {
  HOME_PAGE_ROUTE,
  MESSAGING_PAGE_ROUTE,
} from "../../../common/constants/routerConstants";
import { useAuth } from "../../../common/hooks/useAuth";
import JwtUtils from "../../../common/router/JwtUtils";
import Footer from "../components/Footer";
import config from "../../../config";

const HomePage = () => {
  const { logout } = useContext(AuthContext);
  const { setIsLoading } = useTSData();

  const {
    setMode: setModeApp,
    setMessageActuel,
    isMobile,
    isMidium,
  } = useTSData();
  const { setOpenLoginDialog } = useContext(AuthContext);
  const { isAuthenticated } = useAuth();
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  const [isRecording, setIsRecording] = useState(false);
  const [speechSupported] = useState(true);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }

        await transcribeAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Erreur microphone:", error);
      alert("Impossible d'accéder au microphone");
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");
      const response = await fetch(config.aiChatBotUrl + "/speech-to-text", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Erreur transcription");
      }

      const data = await response.json();

      if (data.text) {
        setMessage((prev) => (prev + " " + data.text).trim());
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la transcription");
    } finally {
      setIsLoading(false);
    }
  };

  const globalStyles = `
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
    
    @keyframes ripple {
      0% { 
        transform: translate(-50%, -50%) scale(0.8);
        opacity: 1;
      }
      100% { 
        transform: translate(-50%, -50%) scale(2);
        opacity: 0;
      }
    }
  `;

  // Injecter les styles
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = globalStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const handleClick = () => {
    if (!message) return;
    setMessageActuel(message);
    setModeApp(MESSAGING_MODE);
    navigate(MESSAGING_PAGE_ROUTE);
  };

  const handleLogout = () => {
    setIsLoading(true);
    logout();
    setIsLoading(false);
    navigate(HOME_PAGE_ROUTE);
  };

  return (
    <TSGrid container>
      <TSGrid item xs={12} sx={{ textAlign: "center" }}>
        <TSGrid container>
          <TSGrid
            item
            xs={12}
            sx={{
              position: "relative",
              textAlign: "center",
            }}
          >
            {/* Background image */}
            <img
              src={robotImgMd}
              alt="decorative robot"
              width={isMobile ? "70px" : "110px"}
              height={isMobile ? "170px" : "210px"}
              style={{
                position: "absolute",
                top: "50%",
                transform: "translate(-100%, -32%)",
                zIndex: 0,
                pointerEvents: "none",
                objectFit: "contain",
                ...(isMidium && { transform: "translate(-100%, -44%)" }),
                ...(isMobile && {
                  left: "50%",
                  transform: "translate(-44%, -64%)",
                }),
              }}
            />

            {/* Text content */}
            <Typography
              variant="tsh5"
              sx={{
                position: "relative",
                zIndex: 1,
                display: "inline-block",
                ...(isMobile && { fontSize: "30px", lineHeight: "35.16px" }),
                ...(isMidium && { fontSize: "33px", lineHeight: "45.16px" }),
              }}
            >
              {isAuthenticated ? "Bonjour " + JwtUtils.getName() : "Bonjour"},
            </Typography>
          </TSGrid>
          <TSGrid item xs={12} sx={{ textAlign: "center" }}>
            <Typography
              variant="tsh3"
              sx={{
                ...(isMobile && { fontSize: "20px", lineHeight: "35.16px" }),
                ...(isMidium && { fontSize: "30px", lineHeight: "45.16px" }),
              }}
            >
              Comment je peux vous aider ?{" "}
            </Typography>
          </TSGrid>
          <TSGrid item xs={12} sx={{ textAlign: "center" }}>
            <TSGrid
              container
              spacing={2}
              justifyContent="center"
              sx={{ mt: 1 }}
            >
              <TSGrid
                item
                xs={12}
                sm={8}
                md={8}
                lg={7}
                sx={{ textAlign: "center" }}
              >
                <TextField
                  name="ts__search"
                  id="ts__search"
                  placeholder="Message TIJARIA..."
                  fullWidth
                  multiline
                  variant="outlined"
                  value={message}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.shiftKey) {
                      setMessage((prevInput) => prevInput + "\n");
                      e.preventDefault();
                    } else if (e.key === "Enter") {
                      handleClick();
                      e.preventDefault();
                    }
                  }}
                  InputProps={{
                    sx: {
                      backgroundColor: "#FFFFFF",
                      borderRadius: "10px",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        {speechSupported && (
                          <IconButton
                            onClick={
                              isRecording ? stopRecording : startRecording
                            }
                            sx={{
                              color: isRecording ? "#f44336" : "#666",
                              backgroundColor: isRecording
                                ? "rgba(244, 67, 54, 0.1)"
                                : "transparent",
                              marginRight: "8px",
                              position: "relative",
                              animation: isRecording
                                ? "pulse 1.5s infinite"
                                : "none",
                              "&::before": isRecording
                                ? {
                                    content: '""',
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    width: "40px",
                                    height: "40px",
                                    backgroundColor: "rgba(244, 67, 54, 0.3)",
                                    borderRadius: "50%",
                                    transform: "translate(-50%, -50%)",
                                    animation: "ripple 1.5s infinite",
                                    zIndex: -1,
                                  }
                                : {},
                            }}
                          >
                            {isRecording ? (
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M6 6h12v12H6z" />
                              </svg>
                            ) : (
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                              </svg>
                            )}
                          </IconButton>
                        )}
                        <IconButton onClick={handleClick} edge="end">
                          <img
                            src={selectIcon}
                            alt="Send Icon"
                            style={{
                              width: "32px",
                              height: "32px",
                              marginRight: "10px",
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </TSGrid>
            </TSGrid>
          </TSGrid>
          {!isAuthenticated ? (
            <TSGrid item xs={12} sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  mt: 2,
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  minWidth: "auto",
                }}
                startIcon={<PermIdentityOutlinedIcon />}
                onClick={setOpenLoginDialog}
              >
                Connexion / Création de compte
              </Button>
            </TSGrid>
          ) : (
            <TSGrid item xs={12} sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  mt: 2,
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  minWidth: "auto",
                }}
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
              >
                Déconnexion
              </Button>
            </TSGrid>
          )}
          <TSGrid item xs={12} sx={{ textAlign: "center" }}>
            <Footer isMidium={isMidium} />
          </TSGrid>
        </TSGrid>
      </TSGrid>
    </TSGrid>
  );
};

export default HomePage;
